/* storybook-check-ignore */
import { useCallback } from 'react';

import { Box, Button, Text } from '@opendoor/bricks-next';
import { formatCurrency } from '@opendoor/shared-fe/formatUtils';

import { PropertyDetailsFragment } from '__generated__/athena';

import { isBuyDirectEligible, useTrackProperty } from 'components/marketplace/helpers';

import { useObservability } from '../../../helpers/observability';
import { CONSUMER_URL } from '../../globals';

const formatPercent = (
  percentage: number,
  minimumFractionDigits?: number,
  maximumFractionDigits?: number,
) =>
  new Intl.NumberFormat('en-US', {
    style: 'percent',
    minimumFractionDigits: minimumFractionDigits ?? 1,
    maximumFractionDigits,
  }).format(percentage);

export const BuyDirectCTA = ({ property }: { property: PropertyDetailsFragment }) => {
  const addressToken = property.addressToken;
  const { trackProperty } = useTrackProperty();
  const { trackJourneyEvent } = useObservability();

  const trackOnBuyDirectClick = useCallback(() => {
    trackProperty('property-buy-direct', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'offerStart', {
      addressUuid: addressToken,
      source: 'marketplace-pdp',
    });
  }, []);

  const trackOnSelfTourClick = useCallback(() => {
    trackProperty('property-self-tour', 'marketplace-pdp');
    trackJourneyEvent('marketplaceProperty', 'tourBookingStart', {
      addressUuid: addressToken,
      placement: 'above-the-fold',
    });
  }, []);

  const isUnderContract =
    property.displayBadge === 'IN_CONTRACT' ||
    property.listingState === 'IN_CONTRACT' ||
    property.listingTags?.some((tag) => tag !== 'BUY_DIRECT_ELIGIBLE');

  const showBuyDirectCard = isBuyDirectEligible(property) && property.buyDirectPrice;

  if (!showBuyDirectCard) {
    return (
      <Box flexDirection="row" justifyContent="space-between">
        <Button
          analyticsName="cosmos-marketplace-self-tour-cta"
          variant="secondary"
          size="small"
          onPress={trackOnSelfTourClick}
          target="_blank"
          rel="noopener"
          label="Self-tour"
          width="48%"
          href={`${CONSUMER_URL}/dashboard/buy/tour?id=${addressToken}`}
        />
      </Box>
    );
  }

  return (
    <Box flexDirection="column" gap="$8x">
      {/* Buy Direct Card */}
      <Box
        backgroundColor="$backgroundSecondary"
        borderWidth={1}
        borderColor="$borderSecondary"
        borderRadius="$7x"
        padding="$12x"
      >
        <Box flexDirection="column" gap="$12x">
          {/* Header Section */}
          <Box flexDirection="column" gap="$1x">
            <Text tag="p" typography="$bodySmall" color="$contentSecondary" fontWeight="$medium">
              Simple and seamless. No surprises.
            </Text>
            <Box flexDirection="row" justifyContent="space-between" alignItems="center">
              <Text tag="h3" typography="$subheaderMedium">
                Buy This Home
              </Text>
              <Box flexDirection="column" alignItems="flex-end" gap="$0x">
                <Text tag="p" typography="$bodyLarge" fontWeight="$medium">
                  {formatCurrency(property.buyDirectPrice!)}
                </Text>
                {property.listingMetadata?.buyDirectDiscountPercent && (
                  <Text tag="p" typography="$bodyXsmall">
                    After {formatPercent(property.listingMetadata.buyDirectDiscountPercent, 0, 2)}{' '}
                    instant discount
                  </Text>
                )}
              </Box>
            </Box>
          </Box>

          {/* Reserve Button */}
          <Button
            analyticsName="cosmos-marketplace-buy-direct-cta"
            variant="primary"
            size="small"
            onPress={trackOnBuyDirectClick}
            rel="noopener"
            label={isUnderContract ? 'Offer Pending' : 'Continue to Checkout'}
            href={`${CONSUMER_URL}/dashboard/buy/checkout?id=${addressToken}`}
            disabled={isUnderContract}
          />
        </Box>
      </Box>

      {/* Self-tour Button */}
      <Box flexDirection="row" justifyContent="space-between">
        <Button
          analyticsName="cosmos-marketplace-self-tour-cta"
          variant="secondary"
          size="small"
          onPress={trackOnSelfTourClick}
          target="_blank"
          rel="noopener"
          label="Self-tour"
          width="100%"
          href={`${CONSUMER_URL}/dashboard/buy/tour?id=${addressToken}`}
        />
      </Box>
    </Box>
  );
};
